import { supabase } from "@/integrations/supabase/client";
import { useHousehold } from "@/hooks/useHousehold";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles, BookOpen, Gamepad2, BarChart3, ShieldCheck, Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { trackMetaEvent } from "@/lib/metaPixel";
import { getErrorMessage } from "@/lib/errors";

type PlanKey =
  | "individual_monthly"
  | "individual_annual"
  | "family_monthly"
  | "family_annual";

const PLANS: Array<{
  key: PlanKey;
  name: string;
  price: string;
  cadence: string;
  blurb: string;
  featured?: boolean;
}> = [
  { key: "individual_monthly", name: "HeirWise Individual", price: "$24.99", cadence: "/month", blurb: "1 teen · full Learning Engine" },
  { key: "individual_annual", name: "HeirWise Individual — Annual", price: "$249", cadence: "/year", blurb: "1 teen · save vs monthly" },
  { key: "family_monthly", name: "HeirWise Family", price: "$39.99", cadence: "/month", blurb: "Up to 4 teens · full Learning Engine" },
  { key: "family_annual", name: "HeirWise Family — Annual", price: "$399", cadence: "/year", blurb: "Up to 4 teens · best value", featured: true },
];


/**
 * PaywallScreen — fast, always-clickable checkout entry.
 *
 * Strategy:
 * 1. Render the page shell immediately (no blocking on household state).
 * 2. In the background, ensure a household exists for the signed-in user.
 *    A household is required by the Stripe checkout edge function.
 * 3. The plan buttons are enabled the moment we have a household ID
 *    (or we'll auto-create one on click as a fallback).
 * 4. Clicking the button calls stripe-billing directly with that household_id.
 */
export default function PaywallScreen() {
  const { user } = useAuth();
  const { household, createHousehold } = useHousehold();
  const [checkingPlan, setCheckingPlan] = useState<PlanKey | null>(null);
  const [bootstrappedId, setBootstrappedId] = useState<string | null>(null);
  const bootstrappingRef = useRef(false);
  const { toast } = useToast();
  const viewedRef = useRef(false);

  // Pricing/paywall view — fire once per mount.
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    void trackMetaEvent("pricing_page_viewed", {
      email: user?.email ?? undefined,
      externalId: user?.id,
      customData: { surface: "paywall_screen" },
    });
  }, [user?.id, user?.email]);

  // Background bootstrap: ensure a household exists so checkout is one click.
  useEffect(() => {
    if (!user || household?.id || bootstrappedId || bootstrappingRef.current) return;
    bootstrappingRef.current = true;
    (async () => {
      try {
        // First, check if one already exists for this user (owner or member).
        const { data: owned } = await supabase
          .from("households")
          .select("id")
          .eq("owner_id", user.id)
          .maybeSingle();
        if ((owned)?.id) {
          setBootstrappedId((owned).id);
          return;
        }
        const created = await createHousehold("My Family");
        if (created?.id) setBootstrappedId(created.id);
      } catch (err) {
        console.warn("[PaywallScreen] background household bootstrap failed:", err);
        // Not fatal — user can still click the button; we'll create on click.
      } finally {
        bootstrappingRef.current = false;
      }
    })();
  }, [user, household?.id, bootstrappedId, createHousehold]);

  const activeHouseholdId = household?.id || bootstrappedId;
  const ready = Boolean(activeHouseholdId);

  const handleSubscribe = async (planKey: PlanKey) => {
    if (checkingPlan) return;
    setCheckingPlan(planKey);
    console.log("[PaywallScreen] Plan selected");
    void trackMetaEvent("checkout_started", {
      email: user?.email ?? undefined,
      externalId: user?.id,
      customData: { plan_key: planKey, surface: "paywall_screen" },
    });
    try {
      let hhId = activeHouseholdId;
      // Fallback: create household on click if background bootstrap didn't finish.
      if (!hhId) {
        console.log("[PaywallScreen] No household yet, creating...");
        const created = await createHousehold("My Family");
        hhId = created?.id;
      }
      if (!hhId) throw new Error("Could not prepare your household. Please try again.");

      console.log("[PaywallScreen] Starting checkout for household:", hhId);
      const { data, error } = await supabase.functions.invoke("stripe-billing", {
        body: { action: "create_checkout", household_id: hhId, plan_key: planKey },
      });
      if (error) throw error;
      if (!data?.url) throw new Error("No checkout URL returned");

      console.log("[PaywallScreen] Redirecting to checkout:", data.url);
      window.location.href = data.url;
    } catch (err: unknown) {
      console.error("[PaywallScreen] Checkout error:", err);
      toast({
        title: "Couldn't start checkout",
        description: getErrorMessage(err) || "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setCheckingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-3xl w-full space-y-8 text-center py-10">
        <div className="w-20 h-20 rounded-3xl bg-primary/10 border-2 border-primary/30 flex items-center justify-center mx-auto">
          <Lock className="w-10 h-10 text-primary" />
        </div>

        <div className="space-y-3">
          <h1 className="font-heading font-bold text-3xl text-foreground">Unlock HeirWise</h1>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Choose the plan that fits your household. Every plan includes the full Learning Engine.
          </p>
        </div>

        <div className="glass-card p-6 space-y-4 text-left max-w-md mx-auto">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">What's included:</p>
          {[
            { icon: BookOpen, text: "Hundreds of lessons with 3,600+ quiz questions and AI-supported coaching" },
            { icon: Gamepad2, text: "Hands-on simulators & interactive modules" },
            { icon: BarChart3, text: "Finance Center — six simulated accounts plus credit score & mastery tracking" },
            { icon: Sparkles, text: "Parent Intelligence Hub — progress, mastery & recommended actions" },
            { icon: ShieldCheck, text: "Cancel anytime" },
          ].map(item => (
            <div key={item.text} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <item.icon className="w-4 h-4 text-primary" />
              </div>
              <p className="text-sm text-foreground pt-1">{item.text}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
          {PLANS.map((plan) => {
            const isLoading = checkingPlan === plan.key;
            return (
              <div
                key={plan.key}
                className={`rounded-2xl border p-5 bg-card/80 backdrop-blur-xl flex flex-col gap-3 ${
                  plan.featured ? "border-primary/60 shadow-glow" : "border-border"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-heading font-semibold text-foreground text-base">{plan.name}</p>
                    <p className="text-xs text-muted-foreground mt-1">{plan.blurb}</p>
                  </div>
                  {plan.featured && (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-primary/15 text-primary border border-primary/30">
                      Best Value
                    </span>
                  )}
                </div>
                <div>
                  <span className="font-heading font-bold text-2xl text-gradient-blue">{plan.price}</span>
                  <span className="text-muted-foreground text-xs ml-1">{plan.cadence}</span>
                </div>
                <Button
                  onClick={() => handleSubscribe(plan.key)}
                  disabled={Boolean(checkingPlan)}
                  variant={plan.featured ? "default" : "outline"}
                  className="w-full"
                  type="button"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Preparing…
                    </span>
                  ) : (
                    "Continue"
                  )}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Individual covers 1 teen. Family covers up to 4 teens. Cancel anytime.
        </p>
        {!ready && !checkingPlan && (
          <p className="text-[11px] text-muted-foreground">Setting things up… you can click anytime.</p>
        )}
      </div>
    </div>
  );
}
